import React, { useState, useEffect } from "react";
import { toast, Toaster } from "react-hot-toast";
import axios from "axios";
import Loading from "../components/Loading";
import { DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Search } from "lucide-react";

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [newUser, setNewUser] = useState({
        name: "",
        email: "",
        phone: "",
        sendInvite: true
    });

    const API_BASE_URL = "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/users";

    const columns = [
        { 
            accessorKey: "username",
            header: "Username",
        },
        { 
            accessorKey: "email",
            header: "Email",
        },
        { 
            accessorKey: "phone",
            header: "Phone",
        },
        { 
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => {
                const status = row.original.status;
                const variant = 
                    status === 'CONFIRMED' ? 'success' :
                    status === 'FORCE_CHANGE_PASSWORD' ? 'warning' :
                    'secondary';
                
                return (
                    <Badge variant={variant}>
                        {status}
                    </Badge>
                );
            }
        },
        {
            id: "actions",
            header: "Actions",
            cell: ({ row }) => (
                <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteClick(row.original)}
                >
                    Delete
                </Button>
            )
        }
    ];

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}`);
            if (response.data.success) {
                setUsers(response.data.users);
            } else {
                toast.error(response.data.error || "Error fetching users");
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Error fetching users");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = async (query) => {
        setSearchQuery(query);
        if (!query.trim()) {
            fetchUsers();
            return;
        }

        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/search?query=${encodeURIComponent(query)}`);
            if (response.data.success) {
                setUsers(response.data.users);
            } else {
                toast.error(response.data.error || "Error searching users");
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Error searching users");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API_BASE_URL}`, newUser);
            
            if (response.data.success) {
                toast.success(response.data.message);
                setShowCreateModal(false);
                setNewUser({ name: "", email: "", phone: "", sendInvite: true });
                fetchUsers();
            } else {
                toast.error(response.data.error || "Error creating user");
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || "Error creating user";
            toast.error(errorMessage);
            if (error.response?.data?.details) {
                console.error("Error details:", error.response.data.details);
            }
        }
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!userToDelete) return;

        try {
            const response = await axios.delete(`${API_BASE_URL}/${userToDelete.username}`);
            if (response.data.success) {
                toast.success(response.data.message);
                fetchUsers();
            } else {
                toast.error(response.data.error || "Error deleting user");
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Error deleting user");
            console.error("Error:", error);
        } finally {
            setShowDeleteDialog(false);
            setUserToDelete(null);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate file type
        if (!file.name.endsWith('.csv')) {
            toast.error("Please upload a CSV file");
            return;
        }

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post(`${API_BASE_URL}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const { summary } = response.data;
                toast.success(
                    `Upload complete: ${summary.successful} successful, ${summary.failed} failed, ${summary.skipped} skipped`
                );
                fetchUsers();
            } else {
                toast.error(response.data.error || "Error uploading users");
            }
        } catch (error) {
            toast.error(error.response?.data?.error || "Error uploading users");
            console.error("Error:", error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    return (
        <div className="p-6">
            <Toaster
                position="bottom-left"
                toastOptions={{
                    style: {
                        background: "#ff8e00",
                        color: "#2e2c2d",
                    },
                }}
            />

            <Card>
                <CardHeader>
                    <CardTitle>User Management</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex justify-between items-center mb-6">
                        <div className="relative w-72">
                            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search users..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="pl-8"
                            />
                        </div>
                        <div className="flex gap-4">
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                variant="default"
                            >
                                Create User
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => document.getElementById('csv-upload').click()}
                            >
                                Upload CSV
                            </Button>
                            <input
                                id="csv-upload"
                                type="file"
                                accept=".csv"
                                className="hidden"
                                onChange={handleFileUpload}
                            />
                        </div>
                    </div>

                    {loading ? (
                        <Loading />
                    ) : (
                        <DataTable
                            columns={columns}
                            data={users}
                            searchable={false}
                        />
                    )}
                </CardContent>
            </Card>

            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New User</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateUser}>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label htmlFor="name">Name</Label>
                                <Input
                                    id="name"
                                    value={newUser.name}
                                    onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={newUser.email}
                                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="phone">Phone (with country code)</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={newUser.phone}
                                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                                    placeholder="+1234567890"
                                />
                            </div>
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="sendInvite"
                                    checked={newUser.sendInvite}
                                    onCheckedChange={(checked) => 
                                        setNewUser({...newUser, sendInvite: checked})
                                    }
                                />
                                <Label htmlFor="sendInvite">Send invitation email</Label>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="submit">Create User</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete the user
                            {userToDelete && ` ${userToDelete.username} (${userToDelete.email})`}.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteConfirm}>
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

export default UserManagement; 